import * as React from "react";
import { Text } from "@uikit";
import Modal from "@components/common/modal";
import Shield_Icon from "@images/icons/review_shield.svg";
import { CourseReview } from "@kalabo/golfsavers-common";
import ReviewAvgItem from "../reviewAvgItem";
import StarRating from "@components/starRating";
import ReviewItem from "../reviewItem";
import { FaCircleNotch } from "@react-icons/all-files/fa/FaCircleNotch";

interface IProps {
  visible?: boolean;
  course_id: string;
  courseName: string;
  onClose?: () => void;
}

const ReviewsModal = ({ visible, course_id, courseName, onClose }: IProps) => {
  const [reviews, setReviews] = React.useState<CourseReview[]>([]);

  React.useEffect(() => {
    if (course_id && visible) {
      loadReviews();
    }
  }, [course_id, visible]);

  const loadReviews = React.useCallback(async () => {
    const response = await fetch(
      "https://golfsavers-functions.azurewebsites.net/api/getCourseReviews",
      {
        method: "POST",
        headers: {
          Accept: "application.json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          courseId: course_id,
        }),
      }
    );

    const data = await response.json();
    if (data) {
      setReviews(data);
    }
  }, [course_id]);

  const getAverageRating = (propName: any) => {
    const ratings = reviews?.map(
      (review) => review[propName as keyof typeof ReviewsModal]
    );
    const sum = ratings.reduce((a, b) => a + b, 0);
    return Number((sum / ratings.length).toFixed(1));
  };

  return (
    <Modal
      visible={visible}
      title={courseName}
      fullScreen={true}
      hideCloseButton={false}
      maxWidth="1020px"
      onClose={() => {
        setReviews([]);
        onClose && onClose();
      }}
    >
      {reviews.length === 0 ? (
        <div className="flex flex-1 justify-center  py-10">
          <FaCircleNotch className="h-8 w-8 text-grey5 animate-spin ml-1" />
        </div>
      ) : (
        <div className="relative grid grid-cols-12 w-full p-5 md:p-10 lg:p-10  gap-2 lg:gap-14">
          <div className="col-span-12 lg:col-span-5">
            <div className="sticky top-10 mb-10">
              <div className="flex gap-2 mb-4 justify-between items-center">
                <div className="flex gap-2 items-center">
                  <div className="flex lg:mt-0">
                    <StarRating
                      mode="small"
                      hideCount
                      rating={getAverageRating("overall")}
                      size="xlarge"
                    />
                  </div>
                  <Text
                    size={21}
                    sizeTablet={21}
                    sizeDesktop={25}
                    weight="semibold"
                    color="black90"
                    className="mr-3"
                  >
                    {getAverageRating("overall")} out of 5 · {reviews?.length}{" "}
                    reviews
                  </Text>
                </div>
              </div>
              <div className="flex flex-col">
                <ReviewAvgItem
                  rating={getAverageRating("condition")}
                  text="Course Condition"
                />
                <ReviewAvgItem
                  rating={getAverageRating("facilities")}
                  text="Facilities"
                />
                <ReviewAvgItem
                  rating={getAverageRating("pace")}
                  text="Pace of Play"
                />
                <ReviewAvgItem
                  rating={getAverageRating("service")}
                  text="Quality of Service"
                />
                <ReviewAvgItem
                  rating={getAverageRating("overall")}
                  text="Overall Experience"
                />
              </div>
              <div className="flex gap-2 items-top">
                <div>
                  <Shield_Icon className="mb-1 inline" />
                </div>
                <div>
                  <Text
                    size={12}
                    weight="semibold"
                    color="black90"
                    className="mb-[2px] mt-1"
                  >
                    100% Verified Reviews
                  </Text>
                  <Text size={11} leading={1.3} color="black60" className="">
                    All reviews on {process.env.GATSBY_SITE_URL} are written by
                    real golfers who have played this golf course
                  </Text>
                </div>
              </div>
            </div>
          </div>{" "}
          <div className="col-span-12 lg:col-span-7 lg:overflow-auto noscrollbar">
            <div className="flex flex-col gap-10 mb-80">
              {reviews
                .sort((a, b) => {
                  if (a.created_at > b.created_at) return -1;
                  return 1;
                })
                .map((review) => (
                  <ReviewItem truncated={false} key={review.id} data={review} />
                ))}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ReviewsModal;
